import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "@store/middleware";

import { getApplicationSpaBackendUrl } from "@utils";
import { type IBifrostFile, type IBifrostFileUploadInput } from "@types";

export const bifrostFilesApiReducerKey = "bifrostFilesApi";

export const bifrostFilesApi = createApi({
  reducerPath: bifrostFilesApiReducerKey,
  baseQuery: (arguments_, api, extraOptions) =>
    baseQueryWithAuth({ baseUrl: getApplicationSpaBackendUrl() })(arguments_, api, {
      ...extraOptions,
      isExternalService: true,
      isUnauthorizedLogoutEnabled: false,
    }),
  endpoints: (builder) => ({
    getBifrostFiles: builder.query<IBifrostFile[], void>({
      query: () => ({
        url: "/bifrost-files",
        method: "GET",
      }),
    }),
    postBifrostFile: builder.mutation<IBifrostFile, IBifrostFileUploadInput>({
      query: ({ file, metadata }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("metadata", JSON.stringify(metadata));

        return {
          url: "/bifrost-files",
          method: "POST",
          body: formData,
        };
      },
      async onQueryStarted(_input, { dispatch, queryFulfilled }) {
        try {
          const { data: createdFile } = await queryFulfilled;

          dispatch(
            bifrostFilesApi.util.updateQueryData("getBifrostFiles", undefined, (files) => {
              files.push(createdFile);
            }),
          );
        } catch {}
      },
    }),
    deleteBifrostFile: builder.mutation<void, IBifrostFile["id"]>({
      query: (id) => ({
        url: `/bifrost-files/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          bifrostFilesApi.util.updateQueryData("getBifrostFiles", undefined, (files) =>
            files.filter((file) => file.id !== id),
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const { useGetBifrostFilesQuery, usePostBifrostFileMutation, useDeleteBifrostFileMutation } = bifrostFilesApi;
